import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import useTranslation from 'common/hooks/useTranslation';
import { user as flagFn, system as systemFlagFn } from 'common/services/flags/api';
import { retrieve as retrieveFn } from 'common/services/session-service';
import { setUser as setFlags, setSystem as setSystemFlags } from 'common/state/flags';
import { getIsLoggedIn } from 'common/state/system';
import { setUser } from 'common/state/user';

import { isAuthenticated as isAuthenticatedFn } from './service';

// useRemoveTrailingSlash strips trailing slashes from the URL. v5 expressed
// this with a regex path on <Redirect from="/:url*(/+)" />, which v6 doesn't
// support — we handle it imperatively here.
const useRemoveTrailingSlash = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.pathname.length > 1 && location.pathname.endsWith('/')) {
      navigate(
        {
          pathname: location.pathname.replace(/\/+$/, ''),
          search: location.search,
          hash: location.hash,
        },
        { replace: true },
      );
    }
  }, [location.pathname, location.search, location.hash, navigate]);
};

const PrivateRoute = ({ children }) => {
  const { isLoggedIn } = useSelector(getIsLoggedIn);
  const dispatch = useDispatch();
  const location = useLocation();
  const { setLanguage } = useTranslation();

  const [isAuthenticated, setAuthenticated] = useState(isLoggedIn);
  const [loading, setLoading] = useState(true);

  useRemoveTrailingSlash();

  useEffect(() => {
    async function effect() {
      setLoading(true);

      if (await isAuthenticatedFn()) {
        const { language, ...user } = await retrieveFn();
        const { data: systemFlags } = await systemFlagFn();
        const { data: flags } = await flagFn(user.userId);
        dispatch([setUser(user), setFlags(flags), setSystemFlags(systemFlags)]);
        setLanguage(language);

        setAuthenticated(true);
      }

      setLoading(false);
    }

    if (!isLoggedIn) {
      effect();
    } else {
      // we already have user information in the redux store.
      // no need to verify for the local session
      setLoading(false);
    }
    // TODO Remove the eslint-disable and fix the problem
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return null;
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to={{
          pathname: '/unauthorized',
          search: `?next=${location.pathname}`,
        }}
        state={{ from: location }}
        replace
      />
    );
  }

  return children;
};

PrivateRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

export default PrivateRoute;
